import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api from "../lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [txn, setTxn] = useState(null);
  const tries = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("no_session"); return; }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {
        // keep polling
      }
      if (tries.current >= 20) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#0F172A] animate-spin" />
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-6">Confirming your payment…</h1>
            <p className="text-zinc-600 mt-2 text-sm">This usually takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-12 h-12 mx-auto grid place-items-center bg-emerald-100 border border-emerald-500 rounded-sm">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mt-6" data-testid="payment-paid-title">
              Payment received.
            </h1>
            <p className="text-zinc-600 mt-3 max-w-md mx-auto">
              Thank you — our UKVI compliance team has been notified and will contact you at your
              chosen email/phone within 2 UK business days.
            </p>
            <div className="mt-6 border border-zinc-200 bg-white rounded-sm p-5 text-left font-mono text-xs inline-block">
              <div><span className="text-zinc-500">Session:</span> {txn?.session_id?.slice(0, 20)}…</div>
              <div><span className="text-zinc-500">Amount:</span> £{txn?.amount} {txn?.currency?.toUpperCase()}</div>
              <div><span className="text-zinc-500">Status:</span> {txn?.payment_status}</div>
            </div>
            <div className="mt-8">
              <Button onClick={() => navigate("/app/history")} className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90" data-testid="payment-back-btn">
                Back to history <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "no_session") && (
          <>
            <div className="w-12 h-12 mx-auto grid place-items-center bg-red-100 border border-red-500 rounded-sm">
              <XCircle className="w-6 h-6 text-red-700" />
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-6">
              {status === "no_session" ? "No payment session" :
                status === "timeout" ? "Still confirming…" : "Payment did not complete"}
            </h1>
            <p className="text-zinc-600 mt-3 text-sm max-w-md mx-auto">
              {status === "timeout"
                ? "It's taking longer than usual. If the amount was charged you'll see it in your history within a few minutes."
                : "If you believe this is a mistake, please contact us."}
            </p>
            <Link to="/app" className="mt-6 inline-block font-mono text-xs underline underline-offset-4">
              Back to dashboard →
            </Link>
          </>
        )}
      </main>
    </div>
  );
}
